import { Component, DestroyRef, Injector, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { BaseModalComponent } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { TranslateService } from '@ngx-translate/core';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { debounceTime, distinctUntilChanged, EMPTY, finalize, startWith, switchMap } from 'rxjs';
import _ from 'lodash';
import { EmployeeCandidate, EmployeeUserCandidate } from '../../../../../models/employee-account/employee-account';
import { EmployeeAccountProxyService } from '../../../../../proxies/employee-account.proxy.service';

/**
 * Add employee (#5775): either create the Standard employee of a Nexus user and enable it in one step, or enable
 * an existing Standard employee. Closes with the employee id so the list can open it.
 */
@Component({
  selector: 'app-add-employee-modal',
  templateUrl: './add-employee-modal.component.html',
})
export class AddEmployeeModalComponent extends BaseModalComponent<string | null> implements OnInit {
  private readonly proxy = inject(EmployeeAccountProxyService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly searchControl = new FormControl<string>('', { nonNullable: true });
  readonly candidates = signal<EmployeeCandidate[]>([]);
  readonly isSearching = signal(false);
  /** The employee being enabled, so its button spins and the others wait. */
  readonly enabling = signal<string | null>(null);

  readonly userControl = new FormControl<EmployeeUserCandidate | null>(null);
  readonly userSuggestions = signal<EmployeeUserCandidate[]>([]);
  readonly creating = signal(false);

  constructor() {
    super(inject(Injector));
  }

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(
        startWith(''),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          this.isSearching.set(true);
          return this.proxy.getCandidates(term.trim() || null).pipe(finalize(() => this.isSearching.set(false)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => this.candidates.set(response.data ?? []));
  }

  enable(candidate: EmployeeCandidate): void {
    this.enabling.set(candidate.employeeReference);
    this.proxy
      .enable(candidate.employeeReference)
      .pipe(finalize(() => this.enabling.set(null)))
      .subscribe((response) => {
        const account = response.data;
        if (_.isNil(account)) {
          return;
        }
        GlobalMessageUtil.success(
          this.label('EmployeeAccount_Title', 'Employees'),
          this.label('EmployeeAccount_Enabled', 'Employee enabled.'),
        );
        this.close({ action: 'save', data: account.id });
      });
  }

  open(candidate: EmployeeCandidate): void {
    this.close({ action: 'save', data: candidate.employeeReference });
  }

  searchUsers(event: AutoCompleteCompleteEvent): void {
    this.proxy.getUserCandidates(event.query.trim() || null).subscribe((response) => this.userSuggestions.set(response.data ?? []));
  }

  /** Creates the user's Standard employee, then enables it with the default accounts. */
  createAndEnable(): void {
    const user = this.userControl.value;
    if (_.isNil(user) || this.creating()) {
      return;
    }
    this.creating.set(true);
    this.proxy
      .createStandard(user.username)
      .pipe(
        switchMap((response) => (_.isNil(response.data) ? EMPTY : this.proxy.enable(response.data.employeeReference))),
        finalize(() => this.creating.set(false)),
      )
      .subscribe((response) => {
        const account = response.data;
        if (_.isNil(account)) {
          return;
        }
        GlobalMessageUtil.success(
          this.label('EmployeeAccount_Title', 'Employees'),
          this.label('EmployeeAccount_Created', 'Employee created and enabled.'),
        );
        this.close({ action: 'save', data: account.id });
      });
  }

  onClose(): void {
    this.close({ action: 'close', data: null });
  }

  getRow(rowData: unknown): EmployeeCandidate {
    return rowData as EmployeeCandidate;
  }

  private label(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }
}
